import { useAuth } from '../contexts/AuthContext';
import { ensureCsrf, getCachedCsrf, clearCsrf } from './csrf';

const formatErrorPart = (value: unknown): string => {
  if (Array.isArray(value)) return value.map(formatErrorPart).filter(Boolean).join('.');
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return '';
};

export const formatApiErrorMessage = (data: any, fallback: string): string => {
  if (!data) return fallback;

  const errorPayload = data.error ?? data;
  if (typeof errorPayload === 'string') return errorPayload;

  if (errorPayload && typeof errorPayload === 'object') {
    const baseMessage = typeof errorPayload.message === 'string' && errorPayload.message.trim()
      ? errorPayload.message
      : fallback;
    const detailSource = errorPayload.details;
    const details = Array.isArray(detailSource) ? detailSource : [];
    const detailMessage = details
      .map((detail) => {
        if (typeof detail === 'string') return detail;
        if (!detail || typeof detail !== 'object') return '';
        const path = formatErrorPart(detail.path ?? detail.field);
        const message = formatErrorPart(detail.message ?? detail.code);
        if (path && message) return `${path}: ${message}`;
        return message || path;
      })
      .filter(Boolean)
      .join('; ');

    return detailMessage ? `${baseMessage}: ${detailMessage}` : baseMessage;
  }

  return fallback;
};

// Unified fetch wrapper that auto-injects X-CSRF-Token for unsafe methods.
export async function apiRequest(
  endpoint: string,
  options: RequestInit & { unsafe?: boolean } = {},
): Promise<any> {
  const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  const url = `${API_BASE_URL}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  const method = (options.method || 'GET').toUpperCase();
  const isUnsafe = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  const headers = new Headers(options.headers as any);
  headers.set('Content-Type', headers.get('Content-Type') || 'application/json');

  if (isUnsafe) {
    const cached = getCachedCsrf();
    const csrf = cached ?? (await ensureCsrf(API_BASE_URL));
    if (!csrf) {
      throw new Error('CSRF unavailable; /auth/me did not return a token.');
    }
    headers.set('X-CSRF-Token', csrf);
  }

  const doFetch = async () => fetch(url, {
    ...options,
    headers,
    credentials: 'include',
  });

  let res = await doFetch();

  if ((res.status === 401 || res.status === 419) && isUnsafe) {
    clearCsrf();
    const fresh = await ensureCsrf(API_BASE_URL);
    if (fresh) {
      headers.set('X-CSRF-Token', fresh);
      res = await fetch(url, { ...options, headers, credentials: 'include' });
    }
  }

  return parseJsonOrThrow(res);
}

async function parseJsonOrThrow(res: Response) {
  const text = await res.text();
  if (!res.ok) {
    let msg = `${res.status} ${res.statusText}`;
    try {
      const data = JSON.parse(text || '{}');
      msg = formatApiErrorMessage(data, msg);
    } catch {}
    throw new Error(msg);
  }
  try { return JSON.parse(text || 'null'); } catch { return null; }
}

// Utility function to create headers with authentication and CSRF token
export const createAuthHeaders = 
  (
  businessId: string,
  token: string | null,
  csrfToken: string | null,
  isCookieMode: boolean,
  additionalHeaders: Record<string, string> = {}
) => {
  console.log('[apiClient] createAuthHeaders called with:', {
    businessId,
    hasToken: !!token,
    hasCsrfToken: !!csrfToken,
    csrfTokenValue: csrfToken ? csrfToken.substring(0, 8) + '...' : 'missing',
    isCookieMode
  });

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...additionalHeaders,
  };

  // Only add Authorization header if not in cookie mode
  if (!isCookieMode && token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // CSRF is injected by global csrfFetch or top-level apiRequest wrapper for unsafe methods

  console.log('[apiClient] Final headers:', Object.keys(headers));

  return headers;
};

// Hook for making authenticated API requests
export const useApiClient = () => {
  const { token, csrfToken, isCookieMode, API_BASE_URL, currentBusiness } = useAuth();

  console.log('[useApiClient] Hook values:', {
    hasToken: !!token,
    hasCsrfToken: !!csrfToken,
    csrfTokenValue: csrfToken ? csrfToken.substring(0, 8) + '...' : 'missing',
    isCookieMode,
    hasApiBaseUrl: !!API_BASE_URL,
    hasCurrentBusiness: !!currentBusiness,
    currentBusinessId: currentBusiness?.id
  });

  const apiRequest = async <T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> => {
    console.log('[useApiClient] apiRequest called for endpoint:', endpoint);
    console.log('[useApiClient] Current csrfToken value when making request:', csrfToken ? csrfToken.substring(0, 8) + '...' : 'null');
    
    if (!currentBusiness) {
      throw new Error('No business context');
    }

    const headers = createAuthHeaders(
      currentBusiness.id,
      token,
      csrfToken,
      isCookieMode,
      options.headers as Record<string, string>
    );

    console.log('[useApiClient] Making request to:', `${API_BASE_URL}${endpoint}`);
    console.log('[useApiClient] Request headers:', Object.keys(headers));

    const response = await fetch(`${API_BASE_URL}${endpoint}`, {
      ...options,
      headers,
      credentials: 'include',
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(formatApiErrorMessage(errorData, `HTTP ${response.status}: ${response.statusText}`));
    }

    return response.json();
  };

  const post = <T>(endpoint: string, data: any): Promise<T> =>
    apiRequest(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });

  const put = <T>(endpoint: string, data: any): Promise<T> =>
    apiRequest(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

  const patch = <T>(endpoint: string, data: any): Promise<T> =>
    apiRequest(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });

  const get = <T>(endpoint: string): Promise<T> =>
    apiRequest(endpoint, { method: 'GET' });

  const _delete = <T>(endpoint: string): Promise<T> =>
    apiRequest(endpoint, { method: 'DELETE' });

  return { post, put, patch, get, delete: _delete };
}; 
